import React, { useState } from 'react';
import { Box, Button, Typography, alpha, useTheme } from '@mui/material';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import dayjs from 'dayjs';
import { PDVThemeColors } from './PDVThemes';
import { useOffline } from '../../shared/offline/useOffline';
import { PainelSincronizacao, IconeConexao, corDaConexao } from '../../shared/offline/PainelSincronizacao';

interface CartaoSincronizacaoProps {
  colors: PDVThemeColors;
}

// Cartão "Sincronização offline" dentro de Configurações do PDV (F10) —
// conexão, lançamentos pendentes e última sincronização; o botão abre o
// painel completo (ver shared/offline/PainelSincronizacao). Fica só aqui, não
// no header (a pedido).
export const CartaoSincronizacao: React.FC<CartaoSincronizacaoProps> = ({ colors }) => {
  const theme = useTheme();
  const { online, pendentes, comErro, sincronizacao } = useOffline();
  const [aberto, setAberto] = useState(false);

  const cor = corDaConexao(online, comErro);
  const situacao = sincronizacao.sincronizando ? 'Sincronizando...' : online ? 'Online' : 'Offline';

  return (
    <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '12px', bgcolor: theme.palette.background.paper, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(colors.primarySolid, 0.12),
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.primarySolid, flexShrink: 0,
        }}>
          <CloudSyncRoundedIcon sx={{ fontSize: 17 }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={700}>Sincronização offline</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Sem internet, as vendas ficam salvas neste computador e são enviadas sozinhas quando a conexão voltar.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 999,
          border: `1px solid ${alpha(cor, 0.6)}`, bgcolor: alpha(cor, 0.08),
        }}>
          <Box sx={{ color: cor, display: 'flex', fontSize: 18 }}>
            <IconeConexao online={online} sincronizando={sincronizacao.sincronizando} />
          </Box>
          <Typography variant="body2" fontWeight={700}>{situacao}</Typography>
        </Box>

        <Typography variant="body2" color={comErro > 0 ? 'error' : 'text.secondary'}>
          {pendentes === 0
            ? 'Nada pendente'
            : `${pendentes} lançamento${pendentes > 1 ? 's' : ''} aguardando envio${comErro > 0 ? ` (${comErro} recusado${comErro > 1 ? 's' : ''})` : ''}`}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Última sincronização: {sincronizacao.ultimaSincronizacao
          ? dayjs(sincronizacao.ultimaSincronizacao).format('DD/MM/YYYY HH:mm')
          : 'nunca'}
      </Typography>

      <Button
        variant="contained"
        onClick={() => setAberto(true)}
        sx={{
          mt: 1.5,
          borderRadius: '10px',
          background: colors.primaryGradient,
          color: colors.textOnPrimary,
          '&:hover': { background: colors.hoverGradient },
        }}
      >
        Abrir sincronização
      </Button>

      <PainelSincronizacao open={aberto} onClose={() => setAberto(false)} />
    </Box>
  );
};
